import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Button,
    Card,
    Checkbox,
    ConfirmDialog,
    Field,
    FileUpload,
    FormActions,
    FormSection,
    InlineBanner,
    Input,
    LanguageTabs,
    PageHeader,
    RadioGroup,
    Skeleton,
    SkeletonRegion,
    StateBlock,
    Switch,
    Textarea,
    notify,
} from '../../components/ui';
import { ALLERGENS, DIETARY_TAGS } from '../../constants/allergens';
import type { Allergen, DietaryTag } from '../../constants/allergens';
import { OWNER_ABOVE } from '../../constants/roles';
import { useApiError } from '../../hooks/useApiError';
import useAuth from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import type { RootState } from '../../store';
import { focusFirstInvalid, useFormErrors } from '../Menus/hooks/useFormErrors';
import { useUnsavedGuard } from '../Menus/hooks/useUnsavedGuard';
import { useAvailability } from './hooks/useAvailability';
import { useRestaurantId, useRestaurantInfo } from './hooks/useRestaurant';
import { useResource } from './hooks/useResource';
import type { AllergenMode, ItemRecord, LangMap } from './types';
import { mapOf, normalizePrice, PRICE_PATTERN, pickName, toTranslations } from './utils';

const DESCRIPTION_MAX = 300;

interface FormState {
    names: LangMap;
    descriptions: LangMap;
    price: string;
    isAvailable: boolean;
    allergenMode: AllergenMode;
    allergens: Allergen[];
    dietaryTags: DietaryTag[];
}

const modeOf = (allergens: ItemRecord['allergens']): AllergenMode => (allergens == null ? 'undeclared' : allergens.length === 0 ? 'none' : 'some');

const toggled = <T extends string>(list: T[], value: T, on: boolean): T[] => (on ? [...list, value] : list.filter((v) => v !== value));

const toFormState = (item: ItemRecord | null, languages: string[]): FormState => {
    const blank = Object.fromEntries(languages.map((l) => [l, '']));
    return {
        names: { ...blank, ...mapOf(item?.translations, 'name') },
        descriptions: { ...blank, ...mapOf(item?.translations, 'description') },
        price: item ? item.price : '',
        isAvailable: item?.isAvailable ?? true,
        allergenMode: item ? modeOf(item.allergens) : 'undeclared',
        allergens: item?.allergens ?? [],
        dietaryTags: item?.dietaryTags ?? [],
    };
};

const FormSkeleton = () => (
    <SkeletonRegion>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="flex flex-col gap-6">
                {[0, 1].map((i) => (
                    <Card key={i} padding="lg" className="flex flex-col gap-4">
                        <Skeleton className="h-5 w-1/4" />
                        <Skeleton className="h-11 w-full" />
                        <Skeleton className="h-24 w-full" />
                    </Card>
                ))}
            </div>
            <Skeleton className="h-56 w-full rounded-card" />
        </div>
    </SkeletonRegion>
);

const MenuItemEditor = ({ itemId }: { itemId?: string }) => {
    const { t } = useTranslation('menu');
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const apiError = useApiError();
    const { hasRole } = useAuth();
    const readOnly = !hasRole(OWNER_ABOVE);
    const previewLang = useSelector((s: RootState) => s.language.current);

    const item = useResource<ItemRecord>(itemId ? `/menu-items/${itemId}` : null);
    const activeRestaurantId = useRestaurantId();
    const restaurantId = itemId ? (item.data?.restaurantId ?? null) : activeRestaurantId;
    const restaurant = useRestaurantInfo(restaurantId);
    const languages = useMemo(() => (restaurant.data?.supportedLanguages.length ? restaurant.data.supportedLanguages : ['en']), [restaurant.data]);

    const failed = item.status === 'error' || restaurant.status === 'error';
    const ready = !failed && restaurant.status === 'ready' && (!itemId || (item.status === 'ready' && item.data?.id === itemId));

    const [state, setState] = useState<FormState | null>(null);
    const [baseline, setBaseline] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState<number | undefined>(undefined);
    const [uploadError, setUploadError] = useState<string | undefined>(undefined);
    /** The saved image; changes at once when the user removes it (that call is not part of Save). */
    const [savedImage, setSavedImage] = useState<string | null>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [lang, setLang] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        if (!ready || state) return;
        const initial = toFormState(item.data, languages);
        setState(initial);
        setSavedImage(item.data?.imageUrl ?? null);
        setBaseline(JSON.stringify(initial));
    }, [ready, state, item.data, languages]);

    const dirty = state !== null && (JSON.stringify(state) !== baseline || file !== null);
    const guard = useUnsavedGuard(dirty && !readOnly);
    const activeLang = lang && languages.includes(lang) ? lang : languages.includes(previewLang) ? previewLang : languages[0];

    const setAvailable = useAvailability((_id, isAvailable) => setState((s) => (s ? { ...s, isAvailable } : s)));

    const errors = useMemo(() => {
        const found: Record<string, string> = {};
        if (!state) return found;
        for (const l of languages) {
            if (!state.names[l]?.trim()) found[`name.${l}`] = t('item.errors.name');
            if ((state.descriptions[l] ?? '').length > DESCRIPTION_MAX)
                found[`description.${l}`] = t('item.errors.descriptionLong', {
                    max: DESCRIPTION_MAX,
                });
        }
        if (!PRICE_PATTERN.test(state.price.trim())) found.price = t('item.errors.price');
        if (state.allergenMode === 'some' && state.allergens.length === 0) found.allergens = t('item.errors.allergens');
        return found;
    }, [state, languages, t]);
    const feedback = useFormErrors(errors);
    const incomplete = feedback.submitted ? languages.filter((l) => errors[`name.${l}`] || errors[`description.${l}`]) : [];

    const patch = (change: Partial<FormState>) => setState((s) => (s ? { ...s, ...change } : s));

    const returnTo = (() => {
        const target = params.get('returnTo');
        return target && target.startsWith('/') && !target.startsWith('//') ? target : '/menu-items';
    })();

    const submit = async () => {
        if (!state || !restaurantId || saving) return;
        if (!feedback.attempt()) {
            const firstBad = languages.find((l) => errors[`name.${l}`] || errors[`description.${l}`]);
            if (firstBad) setLang(firstBad);
            focusFirstInvalid(formRef.current);
            return;
        }
        setSaving(true);
        setSaveError(null);
        const name =
            state.names[activeLang]?.trim() ||
            Object.values(state.names)
                .find((n) => n.trim())
                ?.trim() ||
            '';
        const body = {
            price: normalizePrice(state.price),
            isAvailable: state.isAvailable,
            translations: toTranslations(state.names, state.descriptions),
            allergens: state.allergenMode === 'undeclared' ? null : state.allergenMode === 'none' ? [] : ALLERGENS.filter((a) => state.allergens.includes(a)),
            dietaryTags: DIETARY_TAGS.filter((d) => state.dietaryTags.includes(d)),
        };
        let savedId = itemId;
        try {
            if (itemId) {
                await api.put(`/menu-items/${itemId}`, body);
            } else {
                const { data } = await api.post<{ id: string }>('/menu-items', {
                    restaurantId,
                    ...body,
                });
                savedId = data.id;
            }
        } catch (e) {
            const message = apiError(e);
            setSaveError(message);
            notify.error(message, {
                retry: { label: t('item.retry'), run: () => void submitRef.current() },
            });
            setSaving(false);
            return;
        }

        if (file && savedId) {
            const upload = new FormData();
            upload.append('file', file);
            try {
                setUploadError(undefined);
                setProgress(0);
                await api.post(`/menu-items/${savedId}/upload-image`, upload, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : undefined),
                });
            } catch (e) {
                // The item itself is saved; show the API's reason (type, size, unreadable).
                const message = t('item.photoFailed', { name, reason: apiError(e) });
                notify.error(message);
                setProgress(undefined);
                setSaving(false);
                if (itemId) {
                    setUploadError(message);
                } else {
                    guard.allowLeaving();
                    navigate(`/menu-items/edit/${savedId}`, { replace: true });
                }
                return;
            }
        }
        guard.allowLeaving();
        notify.success(t('item.saved', { name }));
        navigate(returnTo);
    };
    const submitRef = useRef(submit);
    submitRef.current = submit;

    const removePhoto = async () => {
        if (!itemId || removing) return;
        setRemoving(true);
        try {
            await api.delete(`/menu-items/${itemId}/image`);
            setSavedImage(null);
            setUploadError(undefined);
            setConfirmRemove(false);
            notify.success(t('item.photoRemoved'));
        } catch (e) {
            setConfirmRemove(false);
            setUploadError(apiError(e));
        } finally {
            setRemoving(false);
        }
    };

    const shownName = state?.names[activeLang]?.trim() ?? '';
    const heading = shownName || (itemId ? pickName(item.data?.translations, activeLang) || t('items.untitled') : t('item.titleNew'));
    usePageTitle(itemId ? t('item.titleEdit') : t('item.titleNew'));

    return (
        <form
            ref={formRef}
            noValidate
            onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void submit();
            }}
        >
            <PageHeader
                title={heading}
                back={{
                    to: returnTo,
                    label: returnTo === '/menu-items' ? t('items.title') : t('item.back'),
                }}
            />

            {failed && (
                <Card padding="none">
                    <StateBlock
                        variant="error"
                        title={t('item.loadFailed')}
                        body={t('editor.loadFailedBody')}
                        action={
                            <Button
                                onClick={() => {
                                    item.reload();
                                    restaurant.reload();
                                }}
                            >
                                {t('item.retry')}
                            </Button>
                        }
                    />
                </Card>
            )}
            {!failed && !state && <FormSkeleton />}

            {state && (
                <div className="flex flex-col gap-4">
                    {readOnly && <InlineBanner tone="info">{t('item.readOnly')}</InlineBanner>}
                    {!readOnly && feedback.submitted && feedback.count > 0 && (
                        <InlineBanner tone="danger">{t('editor.fieldsNeedAttention', { count: feedback.count })}</InlineBanner>
                    )}
                    {!readOnly && saveError && (
                        <InlineBanner tone="danger" action={{ label: t('item.retry'), onClick: () => void submit() }}>
                            {saveError}
                        </InlineBanner>
                    )}

                    <div className="flex min-w-0 max-w-3xl flex-col gap-6">
                        <FormSection title={t('item.details')}>
                            {languages.length > 1 && <LanguageTabs languages={languages} value={activeLang} onChange={setLang} incomplete={incomplete} />}
                            <Field label={t('item.name')} error={feedback.shown(`name.${activeLang}`)}>
                                {(control) => (
                                    <Input
                                        {...control}
                                        lang={activeLang}
                                        value={state.names[activeLang] ?? ''}
                                        onChange={(e) =>
                                            patch({
                                                names: { ...state.names, [activeLang]: e.target.value },
                                            })
                                        }
                                        onBlur={() => feedback.blur(`name.${activeLang}`)}
                                        disabled={readOnly}
                                        autoComplete="off"
                                    />
                                )}
                            </Field>
                            <Field
                                label={t('item.description')}
                                optional
                                hint={t('item.descriptionCount', {
                                    count: (state.descriptions[activeLang] ?? '').length,
                                    max: DESCRIPTION_MAX,
                                })}
                                error={feedback.shown(`description.${activeLang}`)}
                            >
                                {(control) => (
                                    <Textarea
                                        {...control}
                                        lang={activeLang}
                                        rows={4}
                                        value={state.descriptions[activeLang] ?? ''}
                                        onChange={(e) =>
                                            patch({
                                                descriptions: {
                                                    ...state.descriptions,
                                                    [activeLang]: e.target.value,
                                                },
                                            })
                                        }
                                        onBlur={() => feedback.blur(`description.${activeLang}`)}
                                        disabled={readOnly}
                                    />
                                )}
                            </Field>
                            <div className="sm:max-w-xs">
                                <Field label={t('item.price')} error={feedback.shown('price')}>
                                    {(control) => (
                                        <Input
                                            {...control}
                                            inputMode="decimal"
                                            autoComplete="off"
                                            value={state.price}
                                            onChange={(e) => patch({ price: e.target.value })}
                                            onBlur={() => feedback.blur('price')}
                                            disabled={readOnly}
                                            trailing={<span className="pe-3 text-small text-muted">{restaurant.data?.currencySymbol}</span>}
                                        />
                                    )}
                                </Field>
                            </div>
                            <Switch
                                checked={state.isAvailable}
                                onChange={(next) => {
                                    if (readOnly && itemId) void setAvailable(itemId, heading, next);
                                    else patch({ isAvailable: next });
                                }}
                                label={t('item.available')}
                                description={t('item.availableHint')}
                            />
                        </FormSection>

                        <FormSection title={t('item.photo')}>
                            {readOnly ? (
                                savedImage ? (
                                    <img src={savedImage} alt={heading} loading="lazy" className="aspect-[4/3] w-full rounded-control bg-sunken object-cover" />
                                ) : (
                                    <p className="text-small text-muted">{t('item.noPhoto')}</p>
                                )
                            ) : (
                                <FileUpload
                                    alt={heading}
                                    file={file}
                                    currentUrl={savedImage}
                                    onSelect={(picked) => {
                                        setUploadError(undefined);
                                        setFile(picked);
                                    }}
                                    onRemove={() => {
                                        setUploadError(undefined);
                                        if (file) setFile(null);
                                        else setConfirmRemove(true);
                                    }}
                                    progress={progress}
                                    error={uploadError}
                                    label={t('item.photoOf', { name: heading })}
                                />
                            )}
                        </FormSection>

                        <FormSection title={t('item.allergensTitle')} description={t('item.allergensDescription')}>
                            <div className="flex flex-col gap-2">
                                <RadioGroup<AllergenMode>
                                    aria-label={t('item.allergensTitle')}
                                    value={state.allergenMode}
                                    onChange={(allergenMode) => patch({ allergenMode })}
                                    options={[
                                        {
                                            value: 'undeclared',
                                            label: t('item.mode.undeclared'),
                                            disabled: readOnly,
                                        },
                                        {
                                            value: 'none',
                                            label: t('item.mode.none'),
                                            disabled: readOnly,
                                        },
                                        {
                                            value: 'some',
                                            label: t('item.mode.some'),
                                            disabled: readOnly,
                                        },
                                    ]}
                                />
                                {state.allergenMode === 'undeclared' && <p className="text-caption text-muted">{t('item.modeHint.undeclared')}</p>}
                            </div>
                            {state.allergenMode === 'some' && (
                                <fieldset>
                                    <legend className="sr-only">{t('item.allergensList')}</legend>
                                    <div
                                        tabIndex={-1}
                                        data-invalid={feedback.shown('allergens') ? 'true' : undefined}
                                        className="grid grid-cols-1 gap-x-4 min-[420px]:grid-cols-2 sm:grid-cols-3"
                                    >
                                        {ALLERGENS.map((code) => (
                                            <Checkbox
                                                key={code}
                                                checked={state.allergens.includes(code)}
                                                onChange={(on) =>
                                                    patch({
                                                        allergens: toggled(state.allergens, code, on),
                                                    })
                                                }
                                                label={t(`allergens.${code}`)}
                                                disabled={readOnly}
                                            />
                                        ))}
                                    </div>
                                    {feedback.shown('allergens') && (
                                        <p role="alert" className="mt-2 text-caption text-danger">
                                            {feedback.shown('allergens')}
                                        </p>
                                    )}
                                </fieldset>
                            )}
                            <fieldset>
                                <legend className="mb-1 text-small font-medium text-strong">{t('item.diet')}</legend>
                                <div className="grid grid-cols-1 gap-x-4 min-[420px]:grid-cols-2 sm:grid-cols-3">
                                    {DIETARY_TAGS.map((tag) => (
                                        <Checkbox
                                            key={tag}
                                            checked={state.dietaryTags.includes(tag)}
                                            onChange={(on) =>
                                                patch({
                                                    dietaryTags: toggled(state.dietaryTags, tag, on),
                                                })
                                            }
                                            label={t(`diet.${tag}`)}
                                            disabled={readOnly}
                                        />
                                    ))}
                                </div>
                            </fieldset>
                        </FormSection>
                    </div>

                    {!readOnly && (
                        <FormActions
                            onCancel={() => guard.leave(returnTo)}
                            dirty={dirty}
                            submitting={saving}
                            submitLabel={itemId ? t('item.save') : t('item.create')}
                        />
                    )}
                </div>
            )}
            <ConfirmDialog
                open={confirmRemove}
                onClose={() => setConfirmRemove(false)}
                onConfirm={() => void removePhoto()}
                title={t('item.removePhotoTitle', { name: heading })}
                confirmLabel={t('item.removePhoto')}
                busy={removing}
            >
                {t('item.removePhotoBody')}
            </ConfirmDialog>
            {guard.dialog}
        </form>
    );
};

/** /menu-items/create, /menu-items/edit/:itemId and /menu-items/:id. Roles that cannot edit get the same screen read-only. */
const MenuItemForm = () => {
    const { itemId, id } = useParams();
    const current = itemId ?? id;
    return <MenuItemEditor key={current ?? 'new'} itemId={current} />;
};

export default MenuItemForm;
